import React, { useEffect, useState } from "react";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import PromoterRow from "./PromoterRow";
import PromoterDetail from "./PromoterDetail";

function PromoterAnalytics() {
  const [period, setPeriod] = useState("last_quarter"); // Default period
  const [promoters, setPromoters] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [prsOnly, setPrsOnly] = useState(true); // State for PRs only checkbox
  const [order, setOrder] = useState("desc"); // State for order of total points
  const [detailPromoterId, setDetailPromoterId] = useState(null);
  const [loading, setLoading] = useState(false); // State for loading spinner

  // Overrides state defaults with params parsed from the URL, if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const value = urlParams.get("promoter_id");
    if (value) {
      setDetailPromoterId(value);
    }
  }, []);

  // Fetch data based on period, prsOnly, and order state
  useEffect(() => {
    setLoading(true); // Start loading
    setPromoters([]); // Clear promoters array
    const queryString = `/interact/api/promoter_analytics?period=${period}&prs_only=${prsOnly}&order=${order}`;

    fetch(queryString, {
      cache: "no-store",
      method: "GET",
      headers: new Headers(authenticatedHeadersHash()),
    })
      .then((response) => response.json())
      .then((responseJson) => {
        const { data } = responseJson;

        // Set the start and end times using formatted date strings
        setStartTime(qformat(data.start_time, DATE_FORMATS.date, null));
        setEndTime(qformat(data.end_time, DATE_FORMATS.date, null));

        setPromoters(data.promoters);
        setLoading(false); // Stop loading
      })

      .catch((error) => {
        // eslint-disable-next-line no-alert
        alert(`Error: ${error}`);
        setLoading(false); // Stop loading on error
      });
  }, [period, prsOnly, order]); // Depend on period, prsOnly, and order to refetch when they change

  // Update URL parameters
  useEffect(() => {
    const urlSearchParams = new URLSearchParams();
    if (detailPromoterId) {
      urlSearchParams.append("promoter_id", detailPromoterId);
    }

    window.history.pushState({}, "", `?${urlSearchParams.toString()}`);
  }, [detailPromoterId]);

  // Function to toggle order state between "asc" and "desc"
  function toggleOrder() {
    setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  }

  const detailPromoter = promoters.find((p) => p.profile_id === Number(detailPromoterId));

  return (
    <div>
      <div className="period-selector mb-3">
        <div className="btn-group btn-group-toggle" data-toggle="buttons">
          {[
            "today",
            "yesterday",
            "this_week",
            "last_week",
            "this_month",
            "last_month",
            "this_quarter",
            "last_quarter",
            "this_year",
            "last_year",
            "all_time",
          ].map((p) => (
            <label key={p} className={`btn btn-secondary ${period === p ? "active" : ""}`}>
              <input
                type="radio"
                value={p}
                checked={period === p}
                onChange={(e) => setPeriod(e.target.value)}
                autoComplete="off"
              />{" "}
              {p.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </div>
      {/* "PRs only?" checkbox */}
      <div className="form-check mb-3">
        <input
          type="checkbox"
          className="form-check-input"
          id="prsOnlyCheckbox"
          checked={prsOnly}
          onChange={(e) => setPrsOnly(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="prsOnlyCheckbox">
          PRs only?
        </label>
      </div>
      <div>
        <p>
          Timespan: {startTime}&nbsp;&ndash;&nbsp;{endTime}
        </p>
      </div>
      {loading && (
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      )}
      {detailPromoter && !loading && (
        <PromoterDetail
          promoter={detailPromoter}
          back={() => setDetailPromoterId(null)}
          startTime={startTime}
          endTime={endTime}
        />
      )}
      {!detailPromoter && !loading && (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Promoter</th>
              <th>Email</th>
              <th>Company</th>
              <th>V1 User ID</th>
              <th>V1 User Link</th>
              <th>Pitches</th>
              <th>Conversations</th>
              <th>Bad Behavior Points</th>
              <th onClick={toggleOrder} style={{ cursor: "pointer" }}>
                Total Points {order === "asc" ? "▲" : "▼"}
              </th>
            </tr>
          </thead>
          <tbody>
            {promoters.map((promoter, index) => (
              <PromoterRow
                key={promoter.profile_id}
                promoter={promoter}
                rank={index + 1}
                select={() => setDetailPromoterId(promoter.profile_id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default PromoterAnalytics;
